import React from 'react';
import { ArrowLeft, Shield, Eye, Lock, Database, Globe, FileText, CheckCircle } from 'lucide-react';
import { scrollToTop } from '../utils/scrollToTop';

interface PrivacyPolicyProps {
  onBack?: () => void;
}

export function PrivacyPolicy({ onBack }: PrivacyPolicyProps) {
  const handleBack = () => {
    scrollToTop();
    if (onBack) return onBack();
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <div className="container mx-auto px-4 pt-8">
        <button
          onClick={handleBack}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors mb-8"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Home
        </button>
      </div>
      
      <div className="container mx-auto px-4 pb-16 max-w-4xl">
        <div className="text-center mb-12">
          <div className="w-20 h-20 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
            <Shield className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-5xl font-bold mb-6 text-gray-900">
            Privacy Policy
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Your privacy is our priority. Learn how we protect your data and ensure your content stays private.
          </p>
        </div>

        {/* Key Privacy Highlights */}
        <div className="bg-green-600 rounded-2xl p-8 mb-12">
          <h2 className="text-2xl font-bold text-center mb-6">🔒 100% Private Processing</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-2">No File Uploads</h3>
              <p className="text-base text-white">
                Your files never leave your device. All processing happens locally in your browser.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Database className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Local Processing</h3>
              <p className="text-base text-white">
                Video editing, AI transcription, and all features run entirely on your computer.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Eye className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-2">No Content Access</h3>
              <p className="text-base text-white">
                We cannot see, access, or store any of your videos, images, or captions.
              </p>
            </div>
          </div>
        </div>

        {/* Privacy Policy Content */}
        <div className="space-y-8">
          <section className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm">
            <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
              <FileText className="w-6 h-6 text-blue-400" />
              Information We Collect
            </h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold mb-2 text-blue-700">Analytics Data (Anonymous)</h3>
                <p className="text-gray-600 mb-3">
                  We use Google Analytics to understand how users interact with our website. This includes:
                </p>
                <ul className="list-disc list-inside space-y-1 text-gray-600 ml-4">
                  <li>Page views and navigation patterns</li>
                  <li>Session duration and bounce rate</li>
                  <li>Device type and browser information</li>
                  <li>Geographic location (country/city level)</li>
                </ul>
                <p className="text-base text-gray-600 mt-3">
                  <strong>Important:</strong> Analytics data is completely anonymous and does not include any personal information or content you create.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold mb-2 text-green-700">What We DON'T Collect</h3>
                <ul className="list-disc list-inside space-y-1 text-gray-600 ml-4">
                  <li>Your videos, images, or audio files</li>
                  <li>Captions or text content you create</li>
                  <li>Personal information or account data</li>
                  <li>Any content you process through our tools</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm">
            <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
              <Lock className="w-6 h-6 text-green-400" />
              How We Process Your Content
            </h2>
            <div className="space-y-4">
              <div className="bg-green-500/20 rounded-lg p-4">
                <h3 className="text-xl font-semibold mb-2 text-green-700">100% Client-Side Processing</h3>
                <p className="text-gray-600 mb-3">
                  All video editing, AI transcription, and content processing happens entirely in your browser using:
                </p>
                <ul className="list-disc list-inside space-y-1 text-gray-600 ml-4">
                  <li><strong>FFmpeg.wasm:</strong> Video processing library that runs in your browser</li>
                  <li><strong>Whisper AI:</strong> Speech-to-text model that runs locally</li>
                  <li><strong>Canvas API:</strong> Image processing using your browser's native capabilities</li>
                  <li><strong>WebAssembly:</strong> High-performance processing without server uploads</li>
                </ul>
              </div>
              
              <div className="bg-blue-500/20 rounded-lg p-4">
                <h3 className="text-xl font-semibold mb-2 text-blue-700">No Server Uploads</h3>
                <p className="text-gray-600">
                  Your files are never uploaded to our servers or any third-party services. Everything stays on your device throughout the entire editing process.
                </p>
              </div>
            </div>
          </section>

          <section className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm">
            <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
              <Globe className="w-6 h-6 text-purple-400" />
              Third-Party Services
            </h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold mb-2 text-purple-700">Google Analytics</h3>
                <p className="text-gray-600 mb-3">
                  We use Google Analytics to understand website usage patterns. This service:
                </p>
                <ul className="list-disc list-inside space-y-1 text-gray-600 ml-4">
                  <li>Tracks anonymous website usage statistics</li>
                  <li>Does not access your files or content</li>
                  <li>Uses cookies to remember your preferences</li>
                  <li>Can be disabled through your browser settings</li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold mb-2 text-orange-700">CDN Downloads (One-Time)</h3>
                <p className="text-gray-600 mb-3">
                  When you first use certain features, we download processing libraries to your browser:
                </p>
                <ul className="list-disc list-inside space-y-1 text-gray-600 ml-4">
                  <li><strong>FFmpeg.wasm:</strong> ~30MB video processing library</li>
                  <li><strong>Whisper AI Model:</strong> ~40-75MB speech recognition model</li>
                  <li>These are cached locally and never uploaded</li>
                  <li>Downloaded from trusted CDN sources (unpkg.com, Hugging Face)</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm">
            <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
              <CheckCircle className="w-6 h-6 text-green-400" />
              Your Rights and Choices
            </h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold mb-2 text-green-700">Complete Control</h3>
                <ul className="list-disc list-inside space-y-1 text-gray-600 ml-4">
                  <li>All your content remains on your device</li>
                  <li>No account registration required</li>
                  <li>No data collection or storage</li>
                  <li>You can delete all data by clearing your browser cache</li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold mb-2 text-blue-700">Analytics Opt-Out</h3>
                <p className="text-gray-600">
                  You can disable Google Analytics by installing browser extensions like "uBlock Origin" or "Privacy Badger", 
                  or by adjusting your browser's privacy settings to block tracking cookies.
                </p>
              </div>
            </div>
          </section>

          <section className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm">
            <h2 className="text-2xl font-bold mb-6">Data Security</h2>
            <div className="space-y-4">
              <p className="text-gray-600">
                Since all processing happens locally in your browser, your data security is primarily dependent on your device's security. 
                We recommend:
              </p>
              <ul className="list-disc list-inside space-y-1 text-gray-600 ml-4">
                <li>Keeping your browser and operating system updated</li>
                <li>Using secure, private networks when possible</li>
                <li>Clearing browser cache if you want to remove downloaded libraries</li>
                <li>Using incognito/private browsing mode for maximum privacy</li>
              </ul>
            </div>
          </section>

          <section className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm">
            <h2 className="text-2xl font-bold mb-6">Contact Us</h2>
            <div className="space-y-4">
              <p className="text-gray-600">
                If you have any questions about this Privacy Policy or our privacy practices, please contact us at:
              </p>
              <div className="bg-blue-500/20 rounded-lg p-4">
                <p className="text-blue-700 font-semibold">Email: privacy@sniprush.com</p>
                <p className="text-base text-gray-600 mt-2">
                  We'll respond to privacy-related inquiries within 48 hours.
                </p>
              </div>
            </div>
          </section>

          <section className="bg-blue-600 rounded-2xl p-8 text-center">
            <h2 className="text-2xl font-bold mb-4">Privacy-First Design</h2>
            <p className="text-xl text-white mb-6">
              We built Snip Rush with privacy as the foundation. Your content never leaves your device, 
              and we have no access to your files, videos, or creative work.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={handleBack}
                className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold text-xl hover:bg-gray-100 transition-colors"
              >
                Start Creating Privately
              </button>
            </div>
          </section>
        </div>

        <div className="text-center mt-12 text-base text-gray-600">
          <p>Last updated: {new Date().toLocaleDateString()}</p>
          <p className="mt-2">
            This privacy policy applies to all users of Snip Rush and our video editing tools.
          </p>
        </div>
      </div>
    </div>
  );
}
